import { useCallback, useEffect, useRef, useState } from 'react';

import type { PCMAudioManager, SilenceStateCallback } from '@/lib/pcmAudioManager';
import { Sentry } from '@/lib/sentry';
import { AUTO_STOP_COUNTDOWN_MS, AUTO_STOP_SILENCE_MS, NO_INPUT_WARN_MS } from '@/lib/silenceDetection';
import type { StreamingClient } from '@/lib/streamingClient';

export type SilencePhase = 'idle' | 'no-input' | 'countdown' | 'stopped';

interface UseSilenceMonitorOptions {
  pcmAudioManagerRef: React.RefObject<PCMAudioManager | null>;
  streamingClientRef: React.RefObject<StreamingClient | null>;
  /** Label for log messages, e.g. 'Conversation' or 'Dictation' */
  label: string;
  /** Only active while recording */
  isRecording: boolean;
  /** Called when the countdown elapses — each page passes its pause handler */
  onAutoStop: () => void;
}

interface UseSilenceMonitorReturn {
  phase: SilencePhase;
  secondsRemaining: number;
  /** Pass to pcmAudioManager.onSilenceStateChange() */
  handleSilenceStateChange: SilenceStateCallback;
  /** User clicked "Hætta við" during countdown */
  cancel: () => void;
  /** Call when starting a new recording to clear the stopped banner */
  reset: () => void;
}

const COUNTDOWN_SECONDS = Math.round(AUTO_STOP_COUNTDOWN_MS / 1000);

export const useSilenceMonitor = ({
  pcmAudioManagerRef,
  streamingClientRef: _streamingClientRef,
  label,
  isRecording,
  onAutoStop,
}: UseSilenceMonitorOptions): UseSilenceMonitorReturn => {
  const [phase, setPhase] = useState<SilencePhase>('idle');
  const [secondsRemaining, setSecondsRemaining] = useState(COUNTDOWN_SECONDS);
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phaseRef = useRef<SilencePhase>('idle');
  const onAutoStopRef = useRef(onAutoStop);
  onAutoStopRef.current = onAutoStop;

  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  // Keep phaseRef in sync so we can read current phase without setState updater tricks
  const updatePhase = useCallback((newPhase: SilencePhase) => {
    phaseRef.current = newPhase;
    setPhase(newPhase);
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearCountdown();
    setSecondsRemaining(COUNTDOWN_SECONDS);
    updatePhase('idle');
    // Clear the manager's lastSoundTime so the next 5-min window starts fresh.
    // Otherwise (e.g., after a user resumes from auto-pause) the pre-pause
    // lastSoundTime is still in the past and the next tick instantly re-triggers.
    pcmAudioManagerRef.current?.resetSilenceTracking();
  }, [clearCountdown, updatePhase, pcmAudioManagerRef]);

  const cancel = useCallback(() => {
    clearCountdown();
    setSecondsRemaining(COUNTDOWN_SECONDS);
    updatePhase('idle');
  }, [clearCountdown, updatePhase]);

  const startCountdown = useCallback(() => {
    clearCountdown();
    setSecondsRemaining(COUNTDOWN_SECONDS);
    updatePhase('countdown');
    countdownTimerRef.current = setInterval(() => {
      setSecondsRemaining((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
  }, [clearCountdown, updatePhase]);

  const handleSilenceStateChange = useCallback<SilenceStateCallback>(
    (isSilent, silenceDurationMs, heardSound = true) => {
      if (!isRecordingRef.current) return;
      if (!isSilent) {
        if (phaseRef.current === 'no-input' || phaseRef.current === 'countdown') cancel();
        return;
      }
      if (phaseRef.current !== 'idle') return;
      // Silence before any sound is a dead or muted mic, not a quiet room.
      if (!heardSound) {
        if (silenceDurationMs >= NO_INPUT_WARN_MS) {
          console.warn(`[${label}] No audio input detected since recording started`);
          Sentry.captureMessage('audio_no_input_detected', { level: 'warning' });
          updatePhase('no-input');
        }
      } else if (silenceDurationMs >= AUTO_STOP_SILENCE_MS) {
        startCountdown();
      }
    },
    [label, startCountdown, cancel, updatePhase]
  );

  // Fire the auto-stop as an effect (not inside a setState updater) so it runs
  // exactly once even under StrictMode/concurrent re-invocation.
  useEffect(() => {
    if (secondsRemaining === 0 && phaseRef.current === 'countdown') {
      clearCountdown();
      console.info(`[${label}] Auto-pausing recording after sustained silence`);
      onAutoStopRef.current();
      updatePhase('stopped');
    }
  }, [secondsRemaining, clearCountdown, label, updatePhase]);

  useEffect(() => {
    if (!isRecording) {
      clearCountdown();
    }
    return () => clearCountdown();
  }, [isRecording, clearCountdown]);

  return { phase, secondsRemaining, handleSilenceStateChange, cancel, reset };
};
